<script setup lang="ts">
import axios from 'axios'

definePage({
  meta: {
    icon: 'mdi-wizard-hat',
    title: 'ThingsBoard Test',
    drawerIndex: 99,
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const TB_BASE_URL = import.meta.env.VITE_TB_URL

interface TestResult {
  label: string
  endpoint: string
  status: 'idle' | 'loading' | 'success' | 'error'
  data: unknown
  error: string
  requestInfo: {
    url: string
    headers: Record<string, string>
    responseStatus: number | null
  } | null
}

const isLoading = ref(false)
const tbToken = ref<string | null>(null)
const loginResponse = ref<unknown>(null)
const loginError = ref('')

const { token: keycloakToken } = useKeycloak()
const keycloakTokenCopied = ref(false)
async function copyKeycloakToken() {
  if (!keycloakToken.value) return
  await navigator.clipboard.writeText(keycloakToken.value)
  keycloakTokenCopied.value = true
  setTimeout(() => { keycloakTokenCopied.value = false }, 2000)
}

const tests = ref<TestResult[]>([
  { label: 'Utilisateur courant',      endpoint: '/api/auth/user',                           status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Heure serveur',            endpoint: '/api/dashboard/serverTime',                 status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Info usage tenant',        endpoint: '/api/usage',                                status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Appareils (5 premiers)',   endpoint: '/api/tenant/devices?pageSize=5&page=0',     status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Clients (5 premiers)',     endpoint: '/api/customers?pageSize=5&page=0',           status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Assets (5 premiers)',      endpoint: '/api/tenant/assets?pageSize=5&page=0',      status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Types d\'appareils',       endpoint: '/api/device/types',                         status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Types d\'assets',          endpoint: '/api/asset/types',                          status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Dashboards (5 premiers)',  endpoint: '/api/tenant/dashboards?pageSize=5&page=0',  status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Profils d\'appareils',     endpoint: '/api/deviceProfiles?pageSize=5&page=0',     status: 'idle', data: null, error: '', requestInfo: null },
])

async function runTest(test: TestResult) {
  if (!tbToken.value) return
  test.status = 'loading'
  test.data = null
  test.error = ''
  test.requestInfo = null

  const url = `${TB_BASE_URL}${test.endpoint}`
  const headers: Record<string, string> = {
    'X-Authorization': `Bearer ${tbToken.value}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }

  try {
    const response = await axios.get(url, { headers })
    test.requestInfo = { url, headers, responseStatus: response.status }
    test.data = response.data
    test.status = 'success'
  } catch (e: unknown) {
    const err = e as { response?: { status: number; data: unknown }; message?: string }
    test.requestInfo = { url, headers, responseStatus: err.response?.status ?? null }
    test.error = err.response
      ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
      : (err.message ?? String(e))
    test.status = 'error'
  }
}

async function runAllTests() {
  for (const test of tests.value) {
    await runTest(test)
  }
}

const theme = useTheme()

onMounted(async () => {
  isLoading.value = true
  loginError.value = ''
  try {
    const response = await apiOmni.post('api/tb/login')
    tbToken.value = response.data.token
    loginResponse.value = response.data
    await runAllTests()
  } catch (e: unknown) {
    const err = e as { response?: { status: number; data: unknown }; message?: string }
    loginError.value = err.response
      ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
      : (err.message ?? String(e))
    Notify.error(loginError.value)
  } finally {
    isLoading.value = false
  }
})

const statusColor = (status: TestResult['status']) => {
  switch (status) {
    case 'success': return 'success'
    case 'error':   return 'error'
    case 'loading': return 'warning'
    default:        return 'grey'
  }
}
const statusIcon = (status: TestResult['status']) => {
  switch (status) {
    case 'success': return 'mdi-check-circle'
    case 'error':   return 'mdi-alert-circle'
    case 'loading': return 'mdi-loading'
    default:        return 'mdi-circle-outline'
  }
}
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-api" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">ThingsBoard</div>
            <div class="text-caption text-medium-emphasis">Test de connexion — {{ TB_BASE_URL }}</div>
          </div>
        </div>
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn
            color="primary"
            variant="tonal"
            rounded="pill"
            prepend-icon="mdi-refresh"
            :loading="isLoading"
            @click="runAllTests"
          >
            Relancer les tests
          </v-btn>
        </div>
      </div>
    </div>

    <v-divider/>

    <div style="flex: 1 1 auto; overflow-y: auto;" class="pa-4">

      <!-- Login status -->
      <v-alert v-if="loginError" type="error" variant="tonal" density="compact" class="mb-4">
        Erreur de connexion proxy : {{ loginError }}
      </v-alert>

      <v-row v-if="tbToken" class="mb-4">
        <v-col cols="12" md="6">
          <v-alert type="success" variant="tonal" density="compact" icon="mdi-check-circle" class="mb-2">
            Token TB obtenu via <code>api/tb/login</code>
          </v-alert>
          <v-card variant="outlined" density="compact">
            <v-card-title class="text-body-2">
              <v-icon size="16" class="mr-1">mdi-api</v-icon>
              Réponse complète api/tb/login
            </v-card-title>
            <v-card-text>
              <pre
                class="text-caption overflow-auto pa-2 rounded"
                style="white-space: pre-wrap; word-break: break-all; background: rgba(128,128,128,0.1); max-height: 250px;"
              >{{ JSON.stringify(loginResponse, null, 2) }}</pre>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Keycloak token for Swagger -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card variant="outlined" density="compact">
            <v-card-title class="text-body-2">
              <v-icon size="16" class="mr-1">mdi-shield-key</v-icon>
              JWT Keycloak (pour Swagger)
            </v-card-title>
            <v-card-text>
              <div
                class="pa-2 rounded text-caption"
                style="font-family: monospace; word-break: break-all; white-space: pre-wrap; background: rgba(128,128,128,0.1); max-height: 60px; overflow: auto;"
              >{{ keycloakToken || 'Chargement…' }}</div>
            </v-card-text>
            <v-card-actions>
              <v-btn
                size="small"
                :color="keycloakTokenCopied ? 'success' : 'default'"
                :prepend-icon="keycloakTokenCopied ? 'mdi-check' : 'mdi-content-copy'"
                variant="text"
                @click="copyKeycloakToken"
              >
                {{ keycloakTokenCopied ? 'Copié !' : 'Copier' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Test results -->
      <v-row>
        <v-col
          v-for="test in tests"
          :key="test.endpoint"
          cols="12"
          md="6"
        >
          <v-card variant="outlined">
            <v-card-title class="d-flex align-center text-body-1">
              <v-icon
                :color="statusColor(test.status)"
                :icon="statusIcon(test.status)"
                class="mr-2"
                size="20"
              />
              {{ test.label }}
            </v-card-title>
            <v-card-subtitle class="text-caption" style="font-family: monospace;">
              GET {{ test.endpoint }}
            </v-card-subtitle>
            <v-card-text>
              <template v-if="test.requestInfo">
                <div class="text-caption text-medium-emphasis mb-1 font-weight-bold">Requête</div>
                <pre
                  class="text-caption pa-2 rounded mb-3"
                  style="white-space: pre-wrap; word-break: break-all; background: rgba(128,128,128,0.1);"
                >GET {{ test.requestInfo.url }}

Headers:
{{ JSON.stringify(test.requestInfo.headers, null, 2) }}

Status: {{ test.requestInfo.responseStatus }}</pre>
                <v-divider class="mb-2"/>
              </template>
              <div class="text-caption text-medium-emphasis mb-1 font-weight-bold">Réponse</div>
              <v-alert
                v-if="test.status === 'error'"
                type="error"
                variant="tonal"
                density="compact"
              >
                {{ test.error }}
              </v-alert>
              <pre
                v-else-if="test.data !== null"
                class="text-caption overflow-auto pa-2 rounded"
                style="max-height: 200px; white-space: pre-wrap; word-break: break-all; background: rgba(128,128,128,0.1);"
              >{{ JSON.stringify(test.data, null, 2) }}</pre>
              <span v-else class="text-medium-emphasis text-caption">En attente…</span>
            </v-card-text>
            <v-card-actions>
              <v-btn
                size="small"
                variant="text"
                :disabled="!tbToken || test.status === 'loading'"
                :loading="test.status === 'loading'"
                prepend-icon="mdi-play"
                @click="runTest(test)"
              >
                Retester
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

    </div>
  </v-card>
</template>

<style scoped>
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}
</style>
